import { useState } from "react";
import {
  useGetBuyerCommunicationQuery,
  useAnalyzeConversationMutation,
  useGenerateUpdatedPromptMutation,
  useSaveUpdatedPromptMutation,
} from "apis/buyerCommunication";
import PageLoader from "components/ui/PageLoader";
import Button from "components/ui/Button";
import {
  LuArrowLeft,
  LuCalendar,
  LuCheck,
  LuMessageSquare,
  LuSearch,
  LuThumbsDown,
  LuThumbsUp,
  LuUser,
} from "react-icons/lu";
import Modal from "components/ui/Modal";
import { showToast } from "components/ui/toast";
import { useUpdatePromptMutation } from "apis/prompt";

export default function BuyerCommunication() {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSentiment, setFilterSentiment] = useState("all");
  const [showImproveModal, setShowImproveModal] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("messages");

  // Helper function to safely process changesSummary
  const processChangesSummary = (changesSummary) => {
    if (!changesSummary) {
      return {
        summaryOfChanges: "Prompt has been enhanced based on the suggestion.",
        addedContent: [],
        modifiedSections: [],
      };
    }

    // If changesSummary already has the correct structure, ensure all required fields exist
    return {
      summaryOfChanges:
        changesSummary.summaryOfChanges ||
        "Prompt has been enhanced based on the suggestion.",
      addedContent: Array.isArray(changesSummary.addedContent)
        ? changesSummary.addedContent
        : [],
      modifiedSections: Array.isArray(changesSummary.modifiedSections)
        ? changesSummary.modifiedSections
        : [],
    };
  };

  const {
    data: conversations,
    isPending: isConversationsPending,
    isError: isConversationsError,
    error: conversationsError,
  } = useGetBuyerCommunicationQuery();
  const { mutate: analyzeConversation, isPending: isAnalyzing } =
    useAnalyzeConversationMutation();
  const {
    mutate: generateUpdatedPrompt,
    data: generatedPromptData,
    isPending: isGeneratingPrompt,
    reset: resetGeneratedPrompt,
  } = useGenerateUpdatedPromptMutation();

  const { mutate: saveUpdatedPrompt, isPending: isSavingPrompt } =
    useUpdatePromptMutation();

  const [showPromptConfirmModal, setShowPromptConfirmModal] = useState(false);
  const [currentPromptData, setCurrentPromptData] = useState(null);
  // Sample data based on provided documents

  const filteredConversations = conversations.filter((conv) => {
    // Apply search filter
    const matchesSearch =
      searchQuery === "" ||
      conv?.propertyId?.address
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      conv?.buyerId?.name?.toLowerCase().includes(searchQuery.toLowerCase());

    // Apply sentiment filter
    const matchesSentiment =
      filterSentiment === "all" ||
      (filterSentiment === "positive" &&
        conv.messages.some((m) => m.sentiment === "high")) ||
      (filterSentiment === "negative" &&
        conv.messages.some((m) => m.sentiment === "low")) ||
      (filterSentiment === "needs-review" &&
        conv.messages.some((m) => m.sentiment === "low") &&
        (!conv.analysisResults ||
          (conv.analysisResults &&
            conv.analysisResults.promptSuggestions &&
            conv.analysisResults.promptSuggestions.length > 0)));

    return matchesSearch && matchesSentiment;
  });

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case "high":
        return "bg-green-600 text-white";
      case "low":
        return "bg-red-600 text-white";
      default:
        return "bg-lightgray0 text-white";
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case "high":
        return <LuThumbsUp className="w-3 h-3" />;
      case "low":
        return <LuThumbsDown className="w-3 h-3" />;
      default:
        return null;
    }
  };
  // Get improvement data from the conversation's analysisResults
  const getImprovementData = (conversationId) => {
    const conversation = conversations.find(
      (conv) => conv._id === conversationId
    );

    // If conversation exists and has analysisResults, return those
    if (conversation && conversation.analysisResults) {
      return {
        whatWentWrong: conversation.analysisResults.whatWentWrong || [],
        whatWentRight: conversation.analysisResults.whatWentRight || [],
        actionableImprovements:
          conversation.analysisResults.actionableImprovements || [],
        promptSuggestions: conversation.analysisResults.promptSuggestions || [],
        promptUsed: conversation.analysisResults.promptUsed,
        analyzedAt: conversation.analysisResults.analyzedAt,
      };
    }

    // Return empty arrays if no analysis results found
    return {
      whatWentWrong: [],
      whatWentRight: [],
      actionableImprovements: [],
      promptSuggestions: [],
      promptUsed: "",
      analyzedAt: null,
    };
  }; // Handle the "Improve" button click to start conversation analysis
  const handleImproveClick = () => {
    setShowImproveModal(true);
    // Check if the selected conversation has analysis results already
    const conversation = conversations.find(
      (conv) => conv._id === selectedConversation
    );
    if (!conversation?.analysisResults && !isAnalyzing) {
      startAnalysis();
    }
  };

  // Start the conversation analysis
  const startAnalysis = () => {
    setAnalysisLoading(true);
    analyzeConversation(
      {
        chatId: selectedConversation,
        propertyId: conversations.find(
          (conv) => conv._id === selectedConversation
        )?.propertyId?._id,
        buyerId: conversations.find((conv) => conv._id === selectedConversation)
          ?.buyerId?._id,
        promptName: "REPLY_MESSAGE_PROMPT",
      },
      {
        onSuccess: () => {
          setAnalysisLoading(false);
        },
        onError: (error) => {
          console.error("Analysis error:", error);
          showToast(
            "error",
            "Failed to analyze conversation. Please try again."
          );
          setAnalysisLoading(false);
        },
      }
    );
  }; // Handle generating an updated prompt preview based on suggestion
  const handleApplyPromptSuggestion = (suggestion, promptName) => {
    // Generate the updated prompt to preview
    generateUpdatedPrompt(
      {
        promptName: promptName,
        suggestion: suggestion,
        safeguardInstructions: {
          preserveExistingPrompt: true, // Flag to indicate the existing prompt should be preserved
          enhanceOnly: true, // Only enhance, don't replace core functionality
          addAsSupplement: true, // Add as supplemental instruction instead of replacement
        },
      },
      {
        onSuccess: (data) => {
          // Store the data needed for confirmation        console.log("Generated prompt data:", data);
          setCurrentPromptData({
            suggestion,
            promptName,
            currentPrompt: data.data.data.originalPrompt,
            updatedPrompt: data.data.data.enhancedPrompt,
            changesSummary: processChangesSummary(
              data.data.data.changesSummary
            ),
          });

          // Show the confirmation modal
          setShowPromptConfirmModal(true);
        },
        onError: (error) => {
          console.error("Error generating updated prompt:", error);
          showToast(
            "error",
            "Failed to generate prompt update. Please try again."
          );
        },
      }
    );
  };

  // Handle saving the updated prompt after confirmation
  const handleSaveUpdatedPrompt = () => {
    if (!currentPromptData) return;

    saveUpdatedPrompt(
      {
        name: currentPromptData.promptName,
        description: currentPromptData.updatedPrompt,
        originalPrompt: currentPromptData.currentPrompt,
      },
      {
        onSuccess: () => {
          showToast("success", "Prompt updated successfully");
          setShowPromptConfirmModal(false);
          resetGeneratedPrompt();
        },
        onError: (error) => {
          console.error("Error saving updated prompt:", error);
          showToast("error", "Failed to save prompt update. Please try again.");
        },
      }
    );
  };

  // Improvement Modal component
  const ImproveModal = ({ conversationId, onClose }) => {
    const improvements = getImprovementData(conversationId);
    const conversation = conversations.find(
      (conv) => conv._id === conversationId
    );
    const hasAnalysis = conversation && conversation.analysisResults;
    const isLoading =
      isAnalyzing || analysisLoading || isGeneratingPrompt || isSavingPrompt;
    const [appliedSuggestions, setAppliedSuggestions] = useState([]);

    return (
      <Modal
        onClose={onClose}
        isOpen={showImproveModal}
        title="Conversation Analysis"
        className="max-w-4xl"
      >
        {isLoading ? (
          <div className="flex flex-col items-center justify-center p-10">
            <i className="fa-solid fa-spinner animate-spin mr-2"></i>
            <p className="text-center text-darkgray">
              Analyzing conversation...
            </p>
            <p className="text-center text-sm text-gray-500 mt-2">
              This may take up to a minute. Please wait.
            </p>
          </div>
        ) : !hasAnalysis ? (
          <div className="p-8 text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mx-auto text-gray-400 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9.663 17h4.673M12 3v1m0 16v1m-9-9h1m16 0h1m-1.936-4.66l-.707.707M5.636 5.343l-.707.707m15.556 8.485l-.707.707M5.636 15.242l-.707-.707M17 12a5 5 0 11-10 0 5 5 0 0110 0z"
              />
            </svg>
            <h3 className="text-lg font-medium mb-2">No Analysis Available</h3>
            <p className="text-darkgray mb-6">
              This conversation hasn't been analyzed yet.
            </p>
            <Button
              onClick={startAnalysis}
              className="black-button mx-auto flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Generate Analysis
            </Button>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-start mb-4">
              {improvements.analyzedAt && (
                <p className="text-xs text-gray-500">
                  Analysis performed on{" "}
                  {new Date(improvements.analyzedAt).toLocaleDateString()} at{" "}
                  {new Date(improvements.analyzedAt).toLocaleTimeString()}
                </p>
              )}
              <Button
                onClick={startAnalysis}
                className="gray-button text-xs flex items-center gap-1"
                disabled={isLoading}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Regenerate
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-lightgray p-2 rounded">
                <h4 className="mb-3 flex items-center">
                  <LuThumbsDown className="w-4 h-4 mr-2 text-gray-700" />
                  What Went Wrong
                </h4>
                {improvements.whatWentWrong.length > 0 ? (
                  <ul className="space-y-2">
                    {improvements.whatWentWrong.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-sm text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-lightgray0 italic">
                    No issues identified
                  </p>
                )}
              </div>

              <div className="bg-lightgray p-2 rounded">
                <h4 className=" text-black mb-3 flex items-center">
                  <LuThumbsUp className="w-4 h-4 mr-2 text-gray-700" />
                  What Went Right
                </h4>
                {improvements.whatWentRight.length > 0 ? (
                  <ul className="space-y-2">
                    {improvements.whatWentRight.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-sm text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-lightgray0 italic">
                    No positive aspects identified
                  </p>
                )}
              </div>
            </div>

            <div className="mt-5 bg-white border border-gray-200 p-4 rounded-lg">
              <h4 className=" text-black mb-3">Actionable Improvements</h4>
              {improvements.actionableImprovements.length > 0 ? (
                <ul className="space-y-2">
                  {improvements.actionableImprovements.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-black mt-1.5 mr-2"></span>

                      <span className="text-sm text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-lightgray0 italic">
                  No improvements suggested
                </p>
              )}
            </div>
            <div className="mt-5 bg-lightgray p-4 rounded-lg">
              <h4 className="text-black mb-3 flex justify-between items-center">
                <span>Prompt Suggestions</span>
                {improvements.promptSuggestions.length > 0 && (
                  <span className="text-xs text-gray-500">
                    Click "Apply" to update the prompt with a suggestion
                  </span>
                )}
              </h4>
              {improvements.promptSuggestions.length > 0 ? (
                <ul className="space-y-3">
                  {improvements.promptSuggestions.map((item, index) => {
                    const isApplied = appliedSuggestions.includes(index);
                    return (
                      <li
                        key={index}
                        className={`flex items-start p-2 rounded ${
                          isApplied ? "bg-green-50" : ""
                        }`}
                      >
                        <div className="flex-1">
                          <span className="text-sm text-gray-700">{item}</span>
                        </div>{" "}
                        <button
                          onClick={() => {
                            if (!isApplied) {
                              // Generate the updated prompt preview
                              handleApplyPromptSuggestion(
                                item,
                                improvements.promptUsed
                              );
                            }
                          }}
                          disabled={
                            isGeneratingPrompt || isSavingPrompt || isApplied
                          }
                          className={`ml-2 px-3 py-1 rounded text-xs flex items-center ${
                            isApplied
                              ? "bg-green-600 text-white cursor-default"
                              : "bg-black text-white hover:bg-gray-800"
                          }`}
                        >
                          {isGeneratingPrompt ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          ) : isApplied ? (
                            <>
                              <LuCheck className="w-3 h-3 mr-1" /> Applied
                            </>
                          ) : (
                            "Preview"
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-lightgray0 italic">
                  No prompt suggestions available
                </p>
              )}
            </div>
            {improvements.promptUsed && (
              <div className="mt-5 border border-gray-200 p-4 rounded-lg">
                <h4 className="text-black mb-2">Prompt Used for Analysis</h4>
                <div className="flex items-center mb-2">
                  <span className="text-sm font-medium bg-gray-100 px-3 py-1 rounded">
                    {improvements.promptUsed}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    The suggestions above will improve this prompt
                  </span>
                </div>
                {appliedSuggestions.length > 0 && (
                  <div className="bg-green-50 p-3 rounded mt-3 border border-green-100">
                    <p className="text-sm text-green-800">
                      <span className="font-medium">
                        ✓ {appliedSuggestions.length} suggestion
                        {appliedSuggestions.length > 1 ? "s" : ""} applied
                      </span>
                      <br />
                      <span className="text-xs">
                        The prompt has been updated with your approved
                        suggestions.
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    );
  };
  if (isConversationsPending) {
    return <PageLoader />;
  }
  return (
    <div className="container mx-auto">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left panel - Conversation list */}
        <div
          className={`w-full md:w-1/3 bg-white rounded border transition-all duration-300 ${
            selectedConversation ? "hidden md:block" : ""
          }`}
        >
          <div className="p-5 border-b">
            <h3>Conversations</h3>
            <div className="mt-4 relative">
              <input
                type="text"
                placeholder="Search by property or buyer..."
                className="w-full form-input border rounded"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <LuSearch className="absolute right-3 top-3 h-5 w-5 text-gray-400" />
            </div>{" "}
            <div className="mt-4 flex flex-wrap gap-2">
              {" "}
              <Button
                className={`flex justify-center items-center gap-1 ${
                  filterSentiment === "all" ? "black-button" : "gray-button"
                }`}
                onClick={() => setFilterSentiment("all")}
              >
                All
              </Button>{" "}
              <Button
                className={`flex justify-center gray-button items-center gap-1 ${
                  filterSentiment === "positive"
                    ? "!bg-green-600 text-white"
                    : "gray-button"
                }`}
                onClick={() => setFilterSentiment("positive")}
              >
                <LuThumbsUp className="w-3.5 h-3.5" /> Positive
              </Button>
              <Button
                className={`flex justify-center gray-button items-center gap-1 ${
                  filterSentiment === "negative"
                    ? "!bg-red-600 text-white"
                    : "gray-button"
                }`}
                onClick={() => setFilterSentiment("negative")}
              >
                <LuThumbsDown className="w-3.5 h-3.5" /> Negative
              </Button>
              <Button
                className={`flex justify-center gray-button items-center gap-1 ${
                  filterSentiment === "needs-review"
                    ? "!bg-orange-500 text-white"
                    : "gray-button"
                }`}
                onClick={() => setFilterSentiment("needs-review")}
              >
                Needs Review
              </Button>
            </div>
          </div>
          <div className="divide-y divide-gray-100 max-h-[calc(100vh-14rem)] overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="p-6 text-center text-darkgray">
                <LuMessageSquare className="w-8 h-8 text-darkgray mx-auto mb-2" />
                <p>No conversations found</p>
              </div>
            ) : (
              filteredConversations.map((conversation) => (
                <div
                  key={conversation._id}
                  className={`p-4 hover:bg-lightgray cursor-pointer transition-colors duration-200 ${
                    selectedConversation === conversation._id
                      ? "bg-lightgray border-l-4 border-black"
                      : ""
                  }`}
                  onClick={() => setSelectedConversation(conversation._id)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-black">
                        {conversation.address}
                      </p>
                      <p className=" text-darkgray mt-1 flex items-center gap-1">
                        <LuUser className="w-3.5 h-3.5" />{" "}
                        {conversation?.buyerId?.name}
                      </p>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-lightgray0 flex items-center gap-1">
                        <LuCalendar className="w-3 h-3" />{" "}
                        {formatDate(conversation.createdAt).split(",")[0]}
                      </span>
                      <div className="mt-1 flex items-center gap-1">
                        {" "}
                        {conversation.messages.some(
                          (m) => m.sentiment === "high"
                        ) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-green-600 text-white">
                            <LuThumbsUp className="w-3 h-3 mr-0.5" />
                          </span>
                        )}
                        {conversation.messages.some(
                          (m) => m.sentiment === "low"
                        ) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-red-600 text-white">
                            <LuThumbsDown className="w-3 h-3 mr-0.5" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-lightgray0 mt-2 line-clamp-2">
                    {
                      conversation.messages[conversation.messages.length - 1]
                        .message
                    }
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right panel - Conversation details */}
        {selectedConversation ? (
          <div className="w-full md:w-2/3 bg-white rounded border flex flex-col">
            {(() => {
              const conversation = conversations.find(
                (c) => c._id === selectedConversation
              );
              if (!conversation) return null;

              return (
                <>
                  <div className="p-4 border-b border-lightgray flex justify-between items-center sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                      <Button
                        className="md:hidden p-2 rounded-full hover:bg-gray-100 transition-colors"
                        onClick={() => setSelectedConversation(null)}
                      >
                        <LuArrowLeft className="w-5 h-5" />
                      </Button>
                      <div>
                        <h3>{conversation.address}</h3>
                        <p className="text-sm text-darkgray flex items-center gap-1 mt-0.5">
                          <LuUser className="w-3.5 h-3.5" />{" "}
                          {conversation.buyerId?.name}
                        </p>
                      </div>
                    </div>{" "}
                    <Button
                      onClick={handleImproveClick}
                      className="black-button flex items-center gap-1"
                      disabled={isAnalyzing}
                    >
                      {isAnalyzing ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                          </svg>
                          Improve
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="flex border-b bg-lightgray">
                    <button
                      className={`px-4 py-2 text-sm font-medium focus:outline-none transition ${
                        activeTab === "messages"
                          ? "border-b-2 border-black text-black"
                          : "text-darkgray"
                      }`}
                      onClick={() => setActiveTab("messages")}
                    >
                      Messages
                    </button>
                    <button
                      className={`px-4 py-2 text-sm font-medium focus:outline-none transition ${
                        activeTab === "history"
                          ? "border-b-2 border-black text-black"
                          : "text-darkgray"
                      }`}
                      onClick={() => setActiveTab("history")}
                    >
                      Full History
                    </button>
                  </div>

                  <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
                    {activeTab === "messages" && (
                      <>
                        <div
                          className="w-full md:w-3/5 p-5 overflow-y-auto"
                          style={{ maxHeight: "calc(100vh - 12rem)" }}
                        >
                          <div className="space-y-5">
                            {conversation.messages.map((message, index) => (
                              <div
                                key={index}
                                className={`flex ${
                                  message.sender === "user"
                                    ? "justify-end"
                                    : "justify-start"
                                }`}
                              >
                                {" "}
                                <div
                                  className={`max-w-[85%] p-4 rounded-lg shadow-sm ${
                                    message.sentiment === "high"
                                      ? "bg-green-600 text-white"
                                      : message.sentiment === "low"
                                      ? "bg-red-600 text-white"
                                      : message.sender === "user"
                                      ? "bg-lightgray text-black"
                                      : "bg-black text-white"
                                  }`}
                                >
                                  <div className="flex justify-between items-center mb-1.5 gap-2">
                                    <span className="text-xs font-medium">
                                      {message.sender === "user"
                                        ? conversation.buyerId?.name
                                        : "Sandy"}
                                    </span>{" "}
                                    {message.sentiment && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-white text-black">
                                        {getSentimentIcon(message.sentiment)}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm leading-relaxed">
                                    {message.message}
                                  </p>
                                  <div className="mt-2 text-right">
                                    <span className="text-xs opacity-70">
                                      {formatDate(message.date)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Sentiment Analysis */}
                        <div
                          className="w-full md:w-2/5 border-t md:border-t-0 md:border-l border-gray-200 p-5 overflow-y-auto bg-lightgray"
                          style={{ maxHeight: "calc(100vh - 12rem)" }}
                        >
                          <h4>Sentiment Analysis</h4>

                          <div className="mb-5 bg-white p-4 rounded-lg shadow-sm">
                            <div className="flex justify-between mb-3">
                              <span className="text-sm font-medium">
                                Overall Sentiment
                              </span>
                              <div className="flex items-center gap-1">
                                {" "}
                                {conversation.messages.some(
                                  (m) => m.sentiment === "high"
                                ) ? (
                                  <>
                                    <LuThumbsUp className="w-4 h-4 text-green-600" />
                                    <span className="text-sm font-medium text-green-600">
                                      Positive
                                    </span>
                                  </>
                                ) : conversation.messages.some(
                                    (m) => m.sentiment === "low"
                                  ) ? (
                                  <>
                                    <LuThumbsDown className="w-4 h-4 text-red-600" />
                                    <span className="text-sm font-medium text-red-600">
                                      Negative
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-sm font-medium">
                                    Neutral
                                  </span>
                                )}
                              </div>
                            </div>{" "}
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div
                                className="bg-green-600 h-2.5 rounded-full transition-all duration-500"
                                style={{
                                  width: `${
                                    (conversation.messages.filter(
                                      (m) => m.sentiment === "high"
                                    ).length /
                                      conversation.messages.length) *
                                    100
                                  }%`,
                                }}
                              ></div>
                            </div>
                          </div>

                          <div className="prose prose-sm max-w-none bg-white p-4 rounded-lg shadow-sm mb-5">
                            <div
                              dangerouslySetInnerHTML={{
                                __html:
                                  conversation.summary ||
                                  "<p>No sentiment analysis available</p>",
                              }}
                            />
                          </div>

                          <div className="mt-6">
                            <p className="font-medium">Message Statistics</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white p-4 rounded-lg shadow-sm">
                                <p>Total Messages</p>
                                <p className="font-semibold mt-1">
                                  {conversation.messages.length}
                                </p>
                              </div>
                              <div className="bg-white p-4 rounded-lg shadow-sm">
                                <p>Response Time</p>
                                <p className="font-semibold mt-1">
                                  {conversation.messages.length > 1
                                    ? (() => {
                                        // Parse ISO date strings to Date objects
                                        const first = new Date(
                                          conversation.messages[0].date
                                        );
                                        const second = new Date(
                                          conversation.messages[1].date
                                        );
                                        return `${Math.round(
                                          (second - first) / 1000
                                        )}s`;
                                      })()
                                    : "N/A"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {activeTab === "history" && (
                      <div className="w-full p-5 overflow-y-auto">
                        <h4 className="mb-4 font-semibold">
                          Full Message History
                        </h4>
                        {Array.isArray(conversation.fullHistory) &&
                        conversation.fullHistory.length > 0 ? (
                          <div className="space-y-4">
                            {conversation.fullHistory.map((item, idx) => (
                              <div
                                key={idx}
                                className="bg-lightgray rounded-lg p-4 shadow-sm flex flex-col md:flex-row md:items-center gap-3 border border-gray-100"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-semibold text-black">
                                      {item.role}
                                    </span>
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        item.status === "sent"
                                          ? "bg-green-600 text-white"
                                          : item.status === "pending"
                                          ? "bg-orange-500 text-white"
                                          : "bg-lightgray0 text-black"
                                      }`}
                                    >
                                      {item.status}
                                    </span>
                                    <span className="px-2 py-0.5 rounded-full text-xs bg-mediumgray text-darkergray">
                                      {item.type}
                                    </span>
                                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-darkergray">
                                      {item.channel}
                                    </span>
                                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-darkergray">
                                      Sent At:{" "}
                                      {item.createdAt
                                        ? new Date(
                                            item.createdAt
                                          ).toLocaleString("en-AU", {
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : "-"}
                                    </span>
                                  </div>
                                  <p className="text-sm text-darkergray break-words whitespace-pre-line">
                                    {item.message}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center text-darkgray py-8">
                            No full history available for this conversation.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        ) : (
          <div className="w-full md:w-2/3 bg-white rounded-lg shadow hidden md:flex items-center justify-center p-12">
            <div className="text-center">
              <LuMessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-700">
                Select a conversation
              </h3>
              <p className="text-lightgray0 mt-2 max-w-md">
                Choose a conversation from the list to view the message history
                and sentiment analysis
              </p>
            </div>
          </div>
        )}
      </div>{" "}
      {selectedConversation && showImproveModal && (
        <ImproveModal
          conversationId={selectedConversation}
          onClose={() => setShowImproveModal(false)}
        />
      )}
      {/* Prompt Update Confirmation Modal */}
      {showPromptConfirmModal && currentPromptData && (
        <Modal
          isOpen={showPromptConfirmModal}
          onClose={() => setShowPromptConfirmModal(false)}
          title="Confirm Prompt Update"
          className="max-w-4xl"
        >
          <div className="space-y-4">
            <div className="bg-amber-50 p-4 rounded border border-amber-200 mb-4">
              <p className="text-amber-800 font-medium flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Please review the suggested changes carefully
              </p>
              <p className="text-sm text-amber-700 mt-2">
                This will update the prompt used for generating responses to
                buyers. Only confirm if you're sure these changes improve the
                prompt without losing important instructions.
              </p>
            </div>{" "}
            <div className="bg-blue-50 border border-blue-100 p-4 rounded mb-4">
              <h4 className="text-blue-700 font-medium mb-2 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Changes Summary
              </h4>
              <p className="text-sm text-blue-600 mb-3">
                {currentPromptData.changesSummary.summaryOfChanges}
              </p>

              {/* Show added content when available */}
              {currentPromptData.changesSummary.addedContent &&
                currentPromptData.changesSummary.addedContent.length > 0 && (
                  <div className="mb-3 bg-white bg-opacity-50 p-2 rounded">
                    <h5 className="text-blue-700 text-sm font-medium mb-1 flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Added Content:
                    </h5>
                    <ul className="list-disc pl-5 text-sm text-blue-600">
                      {currentPromptData.changesSummary.addedContent.map(
                        (content, index) => (
                          <li key={`added-${index}`} className="mb-1">
                            {content}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}

              {/* Show modified sections when available */}
              {currentPromptData.changesSummary.modifiedSections &&
                currentPromptData.changesSummary.modifiedSections.length >
                  0 && (
                  <div className="mb-3 bg-white bg-opacity-50 p-2 rounded">
                    <h5 className="text-blue-700 text-sm font-medium mb-1 flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Modified Sections:
                    </h5>
                    <div className="space-y-3">
                      {currentPromptData.changesSummary.modifiedSections.map(
                        (section, index) => (
                          <div key={`section-${index}`} className="pl-5">
                            <h6 className="text-sm font-medium text-blue-700">
                              {section.section}
                            </h6>
                            <p className="text-sm text-blue-600 pl-3 mt-1">
                              {section.modification}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* Show a message when there are no detailed changes */}
              {(!currentPromptData.changesSummary.addedContent ||
                currentPromptData.changesSummary.addedContent.length === 0) &&
                (!currentPromptData.changesSummary.modifiedSections ||
                  currentPromptData.changesSummary.modifiedSections.length ===
                    0) && (
                  <p className="text-sm italic text-blue-500 mt-1">
                    No detailed change information available.
                  </p>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-2 flex items-center">
                  <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs mr-2">
                    Current Prompt
                  </span>
                  {currentPromptData.promptName}
                </h4>
                <div className="bg-white border p-3 rounded h-64 overflow-y-auto">
                  <pre className="text-xs whitespace-pre-wrap">
                    {currentPromptData.currentPrompt}
                  </pre>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2 flex items-center">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs mr-2">
                    Updated Prompt
                  </span>
                  {currentPromptData.promptName}
                </h4>
                <div className="bg-white border border-green-200 p-3 rounded h-64 overflow-y-auto">
                  <pre className="text-xs whitespace-pre-wrap">
                    {currentPromptData.updatedPrompt}
                  </pre>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded border mt-2">
              <h4 className="text-gray-700 mb-2">Applied Suggestion</h4>
              <p className="text-sm bg-gray-100 p-2 rounded">
                {currentPromptData.suggestion}
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button
                className="gray-button"
                onClick={() => {
                  setShowPromptConfirmModal(false);
                  resetGeneratedPrompt();
                }}
              >
                Cancel
              </Button>
              <Button
                className="black-button flex items-center"
                onClick={handleSaveUpdatedPrompt}
                disabled={isSavingPrompt}
              >
                {isSavingPrompt ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Confirm & Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
